import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Snackbar,
  Alert,
  Backdrop,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Zoom
   
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import {
  // Új ikonok a Dialogokhoz:
  LaptopMac as LaptopIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import { motion, useAnimation } from 'framer-motion';
import { throttle } from 'lodash';
import {
  FaLaptopCode,
  FaBookOpen,
  FaCoffee,
  FaPencilAlt,
  FaLightbulb,
  FaStar
} from 'react-icons/fa';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import StarIcon from '@mui/icons-material/Star';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import LeftPanel from '../components/LeftPanel.tsx';
import MyStudyDeskBackground from '../components/MyStudyDeskBackground.tsx';
import BlueScreenOfDeath from '../components/BlueScreenOfDeath.tsx';
import WindowsUpdate from '../components/WindowsUpdate.tsx';
import UbuntuUpdate from '../components/UbuntuUpdate.tsx';
import MacOSUpdate from '../components/MacOSUpdate.tsx';
import { useThemeContext } from '../contexts/ThemeContext.tsx';

const StudyItem = styled(motion.div)({
  position: 'absolute',
  cursor: 'pointer',
  transition: 'all 0.3s ease'
});

const CursorTrail = styled(motion.div)({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  position: 'fixed',
  pointerEvents: 'none',
  zIndex: 9999
});

const LightEffect = styled('div')(({ active }: { active: boolean }) => ({
  position: 'absolute',
  borderRadius: '50%',
  background:
    'radial-gradient(circle, rgba(255,255,0,0.4) 0%, transparent 70%)',
  filter: 'blur(20px)',
  width: '150px',
  height: '150px',
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none',
  opacity: active ? 1 : 0,
  transition: 'opacity 0.3s ease',
  maxWidth: '100%'
}));

// --- Laptop Flow Types ---
type OSOption = 'Windows' | 'Linux' | 'MacOS';

const AnimatedDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  '& .MuiPaper-root': {
    borderRadius: '20px',
    border: darkMode ? '1px solid rgba(255, 0, 0, 0.2)' : '1px solid rgba(255, 215, 0, 0.3)',
    background: darkMode 
        ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
        : 'linear-gradient(45deg, #25379c 30%, #21cbf3 90%)',
    backgroundSize: '200% 200%',
    animation: 'backgroundMove 12s ease infinite',
    color: 'white', // Mivel mindkét gradiens sötétebb tónusú, a fehér szöveg maradjon
    backdropFilter: 'blur(10px)',
    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
  },
  // Keyframes a mozgó háttérhez (ha még nincs benne a fájlban)
  '@keyframes backgroundMove': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  }
}));

const DialogActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  borderRadius: '20px',
  padding: '8px 25px',
  textTransform: 'none',
  fontWeight: 'bold',
  background: 'rgba(255, 255, 255, 0.15)',
  color: 'white',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.25)',
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    background: 'rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.3)',
  }
}));

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  '& .MuiInputBase-root': {
    color: darkMode ? 'white' : '#1a1a1a', // Világos módban sötétszürke szöveg
    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
    borderRadius: '12px',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
    },
  },
  '& .MuiInputLabel-root': {
    color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
  },
  '& .MuiFormHelperText-root': {
    fontWeight: 500,
  },
  '& .MuiFilledInput-underline:before': { borderBottomColor: 'transparent' },
  '& .MuiFilledInput-underline:after': { borderBottomColor: darkMode ? 'gold' : '#1976d2' },
}));




// --- Component ---
const Mystudydesk: React.FC = () => {
  // Common states
  const { darkMode } = useThemeContext();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [leftPanelOpen, setLeftPanelOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState<string | null>(null); // 'coffee', 'laptop', 'book', 'pencil'
  const [timerActive, setTimerActive] = useState(false);
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [customTime, setCustomTime] = useState('');
  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [lampOn, setLampOn] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [urlError, setUrlError] = useState(false);
  

  const isAuthenticated = !!Cookies.get('token');
  const navigate = useNavigate();
  const controls = useAnimation();

  // --- Laptop Flow States ---
  const [laptopStep, setLaptopStep] = useState<number>(0); // 1: OS selection, 2: Sub selection, 3: Timer, 4: Confirmation
  const [laptopMainSelection, setLaptopMainSelection] = useState<OSOption | null>(null);
  const [laptopSubSelection, setLaptopSubSelection] = useState<string | null>(null);
  const [selectedTimeLaptop, setSelectedTimeLaptop] = useState<number | null>(null);
  const [customTimeLaptop, setCustomTimeLaptop] = useState('');
  const [laptopCountdown, setLaptopCountdown] = useState<number>(0);
  const [showLaptopFullScreen, setShowLaptopFullScreen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<null | "update" | "delete">(null);
  

  const isValidUrl = (url: string) => {
  const pattern = new RegExp('^(https?:\\/\\/)?' + 
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + 
    '((\\d{1,3}\\.){3}\\d{1,3}))' + 
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + 
    '(\\?[;&a-z\\d%_.~+=-]*)?' + 
    '(\\#[-a-z\\d%_.~+ Barb-]*)*$', 'i');
  return !!pattern.test(url);
};

const isFormValid = () => {
  if (!editedTool) return false;
  return (
    editedTool.title.trim() !== "" &&
    editedTool.category.trim() !== "" &&
    editedTool.url.trim() !== "" &&
    editedTool.shortDescription.trim() !== "" &&
    !urlError // Az URL-nek érvényesnek is kell lennie
  );
};


  // --- Drawing Tools States ---
  type DrawingTool = {
    id: number;
    category: string;
    shortDescription: string;
    status: string;
    title: string;
    url: string;
  };
  const [drawingToolsData, setDrawingToolsData] = useState<DrawingTool[]>([]);
  const [selectedDrawingTool, setSelectedDrawingTool] = useState<DrawingTool | null>(null);
  const [editedTool, setEditedTool] = useState<DrawingTool | null>(null);
  const [favorites, setFavorites] = useState<number[]>([]);

  const fetchFavorites = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/content/searchmyfavorites`, {
        headers: { 'Authorization': `Bearer ${Cookies.get('token')}` }
      });
      const data = await response.json();
      setFavorites(data.map((item: ContentSearchResultDTO) => item.id));
    } catch (error) {
      console.error("Failed to fetch favorites:", error);
    }
  }, [isAuthenticated]);


  const handleDialogClose = () => {
    setOpenDialog(null);
    setSelectedDrawingTool(null);
    setEditedTool(null);
  };

  const handleConfirmDelete = () => {
    handleDeleteContent();
    showSnackbar("Content deleted successfully", "success");
    setConfirmDialogOpen(null);
    handleDialogClose();
  };
  
  const handleConfirmUpdate = () => {
    handleDrawingToolUpdate();
    showSnackbar("Content updated successfully", "success");
    setConfirmDialogOpen(null);
    handleDialogClose();
  };

  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Fetch drawing tools from API (only PENDING)
  useEffect(() => {
    if (openDialog === 'pencil' && isAuthenticated) {
      fetch(`${process.env.REACT_APP_API_URL}/v1/content/searchmycontent`, {
          headers: { 'Authorization': `Bearer ${Cookies.get('token')}` }
        })
        .then((res) => res.json())
      .then((data: DrawingTool[]) => {
        const pending = data.filter((item) => item.status === 'PENDING');
        setDrawingToolsData(pending);
      })
      .catch((err) => console.error(err));
  } else {
    setDrawingToolsData([]); // Üresítjük az adatokat ha nincs hitelesítés
  }
}, [openDialog, isAuthenticated]);

  // Reset laptop flow when closing dialog
  const resetLaptopFlow = () => {
    setLaptopStep(0);
    setLaptopMainSelection(null);
    setLaptopSubSelection(null);
    setSelectedTimeLaptop(null);
    setCustomTimeLaptop('');
    setLaptopCountdown(0);
    setShowLaptopFullScreen(false);
  };

  // Request or exit true full-screen mode using the Fullscreen API
  useEffect(() => {
    if (showLaptopFullScreen) {
      document.documentElement.requestFullscreen().catch((err) =>
        console.error("Error attempting to enable full-screen mode:", err)
      );
    } else if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) =>
        console.error("Error exiting full-screen mode:", err)
      );
    }
  }, [showLaptopFullScreen]);

  // Mouse tracking: only update if timer is not active
  const handleMouseMove = useCallback(
    throttle((e: MouseEvent) => {
      if (!timerActive) {
        setMousePosition({ x: e.clientX, y: e.clientY });
      }
    }, 50),
    [timerActive]
  );

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove as EventListener);
    return () =>
      window.removeEventListener('mousemove', handleMouseMove as EventListener);
  }, [handleMouseMove]);

  // Timer logic for Coffee Dialog
  useEffect(() => {
    if (timerActive && remainingTime > 0) {
      const timer = setInterval(() => {
        setRemainingTime((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (remainingTime === 0 && timerActive) {
      setTimerActive(false);
      setOpenDialog(null);
    }
  }, [timerActive, remainingTime]);

  const calculateParallax = (x: number, y: number, depth = 20) => {
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    return {
      x: (x - centerX) / depth,
      y: (y - centerY) / depth
    };
  };

  const handleCoffeeStart = () => {
    const time =
      selectedTime ||
      (customTime && !isNaN(parseInt(customTime, 10))
        ? parseInt(customTime, 10) * 60
        : 0);
    setRemainingTime(time);
    setTimerActive(true);
  };

  // --- Laptop Flow Handlers ---
  const startLaptopFlow = () => {
    setOpenDialog('laptop');
    setLaptopStep(1);
  };

  const handleLaptopTimerStart = () => {
    const time =
      selectedTimeLaptop ||
      (customTimeLaptop && !isNaN(parseInt(customTimeLaptop, 10))
        ? parseInt(customTimeLaptop, 10) * 60
        : 0);
    setLaptopCountdown(time);
    setShowLaptopFullScreen(true);
    setOpenDialog(null);
  };

  // Render full-screen update view based on OS and sub-selection
  const renderLaptopUpdateScreen = () => {
    if (laptopMainSelection === 'Windows') {
      if (laptopSubSelection === 'Blue Screen') {
        const durationInMinutes = laptopCountdown;
        return <BlueScreenOfDeath duration={durationInMinutes} />;
      } else if (laptopSubSelection === 'Update Screen') {
        return <WindowsUpdate duration={laptopCountdown} />;
      }
    } else if (laptopMainSelection === 'Linux') {
      if (laptopSubSelection === 'Ubuntu') {
        return <UbuntuUpdate duration={laptopCountdown} />;
      }
      // Fallback for other Linux sub-options:
      return (
        <Box
          sx={{
            height: '100vh',
            bgcolor: 'black',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography variant="h3">Linux Update Screen</Typography>
        </Box>
      );
    } else if (laptopMainSelection === 'MacOS') {
      if (laptopSubSelection === 'Update MacOS X') {
        return <MacOSUpdate duration={laptopCountdown} />;
      }
      // Fallback for other MacOS cases:
      return (
        <Box
          sx={{
            height: '100vh',
            bgcolor: 'black',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography variant="h3">MacOS Update Screen</Typography>
        </Box>
      );
    }
    // Fallback view
    return (
      <Box
        sx={{
          height: '100vh',
          bgcolor: 'black',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography variant="h3">Other OS update screen</Typography>
      </Box>
    );
  };

  const renderLaptopDialogContent = () => {
  // Stílusok a szövegekhez a gradiens háttéren (mindig fehér a jó kontraszt miatt)
  const titleStyle = { color: '#ffffff', fontWeight: 'bold', mb: 1 };
  const textStyle = { color: 'rgba(255,255,255,0.9)', mb: 2 };
  const subTextStyle = { color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' };
  
  const outlinedButtonStyle = { 
    color: '#ffffff', 
    borderColor: 'rgba(255,255,255,0.3)',
    borderRadius: '15px',
    textTransform: 'none',
    px: 3,
    '&:hover': { 
      borderColor: '#ffffff', 
      bgcolor: 'rgba(255,255,255,0.1)',
      transform: 'translateY(-1px)'
    },
    '&.Mui-disabled': {
      color: 'rgba(255,255,255,0.2)',
      borderColor: 'rgba(255,255,255,0.1)'
    }
  };

  switch (laptopStep) {
    case 1:
      return (
        <Box>
          <Typography variant="h5" sx={titleStyle}>Laptop Settings</Typography>
          <Typography variant="body1" sx={textStyle}>Please select your OS.</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            {(['Windows', 'Linux', 'MacOS'] as OSOption[]).map((os) => (
              <Button
                key={os}
                variant={laptopMainSelection === os ? 'contained' : 'outlined'}
                onClick={() => setLaptopMainSelection(os)}
                sx={laptopMainSelection === os ? { borderRadius: '15px', textTransform: 'none', px: 3 } : outlinedButtonStyle}
              >
                {os}
              </Button>
            ))}
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
            <DialogActionButton
              darkMode={darkMode}
              variant="contained"
              onClick={() => laptopMainSelection && setLaptopStep(2)}
              disabled={!laptopMainSelection}
            >
              Next
            </DialogActionButton>
          </Box>
        </Box>
      );

    case 2: {
      let options: string[] = [];
      if (laptopMainSelection === 'Windows') options = ['Update Screen', 'Blue Screen'];
      else if (laptopMainSelection === 'Linux') options = ['Ubuntu'];
      else if (laptopMainSelection === 'MacOS') options = ['Update MacOS X'];

      return (
        <Box>
          <Typography variant="h5" sx={titleStyle}>{laptopMainSelection} Options</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            {options.map((opt) => (
              <Button
                key={opt}
                variant={laptopSubSelection === opt ? 'contained' : 'outlined'}
                onClick={() => setLaptopSubSelection(opt)}
                sx={laptopSubSelection === opt ? { borderRadius: '15px', textTransform: 'none', px: 3 } : outlinedButtonStyle}
              >
                {opt}
              </Button>
            ))}
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button variant="text" onClick={() => setLaptopStep(1)} sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}>
              Back
            </Button>
            <DialogActionButton
              darkMode={darkMode}
              variant="contained"
              onClick={() => laptopSubSelection && setLaptopStep(3)}
              disabled={!laptopSubSelection}
            >
              Next
            </DialogActionButton>
          </Box>
        </Box>
      );
    }

    case 3:
      return (
        <Box>
          <Typography variant="h5" sx={titleStyle}>Set Update Timer</Typography>
          <Typography variant="body2" sx={subTextStyle}>Select a duration for the update screen.</Typography>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Stack direction="row" spacing={2}>
              {[5, 10, 15].map((time) => (
                <Button
                  key={time}
                  variant={selectedTimeLaptop === time * 60 ? 'contained' : 'outlined'}
                  onClick={() => {
                    setSelectedTimeLaptop(time * 60);
                    setCustomTimeLaptop('');
                  }}
                  sx={selectedTimeLaptop === time * 60 ? { borderRadius: '15px', textTransform: 'none' } : outlinedButtonStyle}
                >
                  {time} min
                </Button>
              ))}
            </Stack>
            
            <StyledTextField
              darkMode={darkMode}
              type="number"
              label="Custom Time (min)"
              variant="filled"
              fullWidth
              InputProps={{ 
                disableUnderline: true,
                inputProps: { min: 0, max: 180, style: { color: 'white' } } 
              }}
              InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
              value={customTimeLaptop}
              onFocus={() => setSelectedTimeLaptop(null)}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') { setCustomTimeLaptop(''); return; }
                const numericValue = parseInt(value, 10);
                if (isNaN(numericValue) || numericValue < 0) setCustomTimeLaptop('0');
                else if (numericValue > 180) setCustomTimeLaptop('180');
                else setCustomTimeLaptop(value);
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button variant="text" onClick={() => setLaptopStep(2)} sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}>
                Back
              </Button>
              <DialogActionButton
                darkMode={darkMode}
                variant="contained"
                onClick={() => setLaptopStep(4)}
                disabled={!(selectedTimeLaptop || customTimeLaptop !== '')}
              >
                Next
              </DialogActionButton>
            </Box>
          </Stack>
        </Box>
      );

    case 4: {
      const timerValue = selectedTimeLaptop || (customTimeLaptop ? parseInt(customTimeLaptop, 10) * 60 : 0);
      return (
        <Box>
          <Typography variant="h5" sx={titleStyle}>Confirm Your Settings</Typography>
          <Box sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.1)', 
            p: 2.5, 
            borderRadius: '16px', 
            mb: 3,
            border: '1px solid rgba(255,255,255,0.1)' 
          }}>
            <Typography variant="body1" sx={textStyle}><strong>OS:</strong> {laptopMainSelection}</Typography>
            <Typography variant="body1" sx={textStyle}><strong>Mode:</strong> {laptopSubSelection}</Typography>
            <Typography variant="body1" sx={textStyle}>
              <strong>Duration:</strong> {Math.floor(timerValue / 60)} min {timerValue % 60} sec
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button variant="text" onClick={() => setLaptopStep(3)} sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}>
              Back
            </Button>
            <DialogActionButton
              darkMode={darkMode}
              variant="contained"
              onClick={handleLaptopTimerStart}
              disabled={timerValue <= 0}
            >
              Start Session
            </DialogActionButton>
          </Box>
        </Box>
      );
    }
    default:
      return null;
  }
};

  // --- Drawing Tools Dialog Handler ---
  const handleDrawingToolSelect = (id: number) => {
    const tool = drawingToolsData.find((t) => t.id === id) || null;
    setSelectedDrawingTool(tool);
    setEditedTool(tool); // initialize the editable copy
  };

  const handleDrawingToolUpdate = () => {
    if (editedTool) {
      fetch(`${process.env.REACT_APP_API_URL}/v1/content/updatemycontent`, {
        method: 'PUT',
        headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${Cookies.get('token')}`
      },
        body: JSON.stringify(editedTool)
      })
        .then((res) => res.json())
        .then((data) => {
          setOpenDialog(null);
        })
        .catch((err) => console.error(err));
    }
  };

  const handleDeleteContent = async () => {
    if (!selectedDrawingTool) return;
  
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/content/deletemycontent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.get('token')}`
        },
        body: JSON.stringify({ submissionId: selectedDrawingTool.id })
      });
      if (response.ok) {
        setOpenDialog(null);
      } else {
        alert('Failed to delete content');
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };
  

  const studyItems = [
    {
      icon: <FaLaptopCode size={40} />,
      position: { x: '10%', y: '60%' },
      label: 'Laptop',
      onClick: startLaptopFlow
    },
    {
      icon: <FaBookOpen size={30} />,
      position: { x: '60%', y: '40%' },
      label: 'Books',
      onClick: () => setOpenDialog('book')
    },
    {
      icon: <FaCoffee size={25} />,
      position: { x: '30%', y: '40%' },
      label: 'Coffee',
      onClick: () => setOpenDialog('coffee')
    },
    {
      icon: <FaPencilAlt size={20} />,
      position: { x: '60%', y: '60%' },
      label: 'Pencils',
      onClick: () => setOpenDialog('pencil')
    },
    {
      icon: <FaLightbulb size={25} color={lampOn ? '#ffeb3b' : '#666'} />,
      position: { x: '90%', y: '20%' },
      label: 'Lamp',
      onClick: () => setLampOn(!lampOn)
    }
  ];

  // Define a type for Book Favorites
  type ContentSearchResultDTO = {
    id: number;
    category: string;
    shortDescription: string;
    title: string;
    url: string;
    views: number;
  };

  const [favoritesData, setFavoritesData] = useState<ContentSearchResultDTO[]>([]);

  useEffect(() => {
    if (openDialog === 'book' && isAuthenticated) {
      fetch(`${process.env.REACT_APP_API_URL}/v1/content/searchmyfavorites`, {
        headers: {
          'Authorization': `Bearer ${Cookies.get('token')}`
        }
      })
        .then((res) => res.json())
        .then((data: ContentSearchResultDTO[]) => {
          setFavoritesData(data);
        })
        .catch((err) => console.error(err));
    }
  }, [openDialog, isAuthenticated]);

  const switchFavorite = async (submissionId: number) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/v1/content/switchmyfavorites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.get('token')}`
        },
        body: JSON.stringify({ 
          submissionId: submissionId,
          favorite: 0 // Mindig töröljük, mivel csak kedvencek jelennek meg
        })
      });
      
      // Szűrd ki az eltávolított elemet
      setFavoritesData(prev => prev.filter(item => item.id !== submissionId));
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
    }
  };

    useEffect(() => {
      if (openDialog === 'book') {
        fetchFavorites();
      }
    }, [openDialog, fetchFavorites]);

    const handlePanelAction = (path: string) => {
      if (path === '/learning') {
        navigate('/learning'); 
      } else if (!isAuthenticated) {
        navigate('/login'); 
      } else {
        navigate(path);
      }
    };

    const formatUrl = (url: string) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `https://${url}`;
};

  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      {/* Dark backdrop */}
      <Backdrop
        open={true}
        sx={{
          backgroundColor: lampOn ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.3)',
          zIndex: 5,
          backdropFilter: lampOn ? 'blur(5px)' : 'blur(2px)',
          transition: 'all 0.3s ease'
        }}
      />

      {/* NavBar layer */}
      <div style={{ position: 'relative', zIndex: 100 }}>
        {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}
      </div>

      {/* Background layer */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: -10 }}>
        <MyStudyDeskBackground />
      </div>

      {/* Icon layer */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 10 }}>
        {studyItems.map((item, index) => (
          <StudyItem
            key={index}
            style={{ left: item.position.x, top: item.position.y }}
            animate={timerActive ? { x: 0, y: 0 } : calculateParallax(mousePosition.x, mousePosition.y)}
            whileHover={{
              scale: 1.1,
              rotate: item.label === 'Coffee' ? 0 : 5,
              filter: lampOn ? 'drop-shadow(0 0 8px rgba(255,255,0,0.5))' : 'none'
            }}
            transition={{ type: 'spring', stiffness: 300 }}
            onClick={item.onClick}
          >
            {lampOn && (
              <LightEffect
                active={lampOn}
                style={{
                  left: '50%',
                  top: '50%',
                  zIndex: -1
                }}
              />
            )}
            {item.icon}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                color: '#fff',
                fontWeight: 'bold',
                textShadow: lampOn ? '0 0 8px rgba(255,255,0,0.8)' : '1px 1px 2px rgba(0,0,0,0.8)'
              }}
            >
              {item.label}
            </Typography>
          </StudyItem>
        ))}
      </div>

      <AnimatedDialog
        open={openDialog === 'coffee'}
        /* Az onClose-t is blokkoljuk, ha megy a timer, így véletlenül sem zárható be */
        onClose={() => !timerActive && setOpenDialog(null)}
        disableScrollLock
        darkMode={darkMode}
        TransitionComponent={Zoom}
        transitionDuration={400}
      >
        <DialogTitle sx={{ color: 'white', textAlign: 'center', pt: 3 }}>
          <FaCoffee style={{ marginRight: '8px' }} /> Coffee Break
        </DialogTitle>
        
        <DialogContent sx={{ textAlign: 'center', overflow: 'hidden' }}>
          {!timerActive ? (
            <>
              <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255,255,255,0.9)' }}>
                Take a short break and recharge.
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} justifyContent="center">
                  {[5, 10, 15].map((time) => (
                    <DialogActionButton
                      key={time}
                      darkMode={darkMode}
                      onClick={() => { setSelectedTime(time * 60); setCustomTime(''); }}
                      sx={{ 
                        border: selectedTime === time * 60 ? '2px solid gold' : '1px solid rgba(255,255,255,0.3)',
                        minWidth: '60px'
                      }}
                    >
                      {time}m
                    </DialogActionButton>
                  ))}
                </Stack>
                <StyledTextField
                  darkMode={darkMode}
                  fullWidth
                  type="number"
                  label="Custom Minutes"
                  variant="filled"
                  value={customTime}
                  onChange={(e) => { setCustomTime(e.target.value); setSelectedTime(null); }}
                  InputProps={{ disableUnderline: true, style: { color: 'white' } }}
                  InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                />
                <DialogActionButton 
                  darkMode={darkMode} 
                  onClick={handleCoffeeStart}
                  disabled={!(selectedTime || customTime !== '')}
                  sx={{ width: '100%', py: 1.5, fontWeight: 'bold' }}
                >
                  Start Break
                </DialogActionButton>
              </Stack>
            </>
          ) : (
            <Box sx={{ py: 2, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              
              {/* --- SMOOTH GŐZ KONTÉNER --- */}
              <Box 
                sx={{ 
                  height: '80px', // Visszavéve, hogy ne lógjon a címbe
                  position: 'relative', 
                  width: '100%', 
                  overflow: 'hidden', 
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-end',
                  mb: -1 
                }}
              >
                {/* Első gőzfelhő */}
                <motion.div
                  style={{ position: 'absolute' }}
                  animate={{
                    opacity: [0, 0.4, 0],
                    y: [0, -70],
                    x: [-8, 8, -8],
                    scale: [0.8, 1.3]
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <Typography variant="h3" sx={{ filter: 'blur(5px)', userSelect: 'none', opacity: 0.6 }}>
                    ☁️
                  </Typography>
                </motion.div>

                {/* Második gőzfelhő */}
                <motion.div
                  style={{ position: 'absolute' }}
                  animate={{
                    opacity: [0, 0.3, 0],
                    y: [0, -60],
                    x: [8, -8, 8],
                    scale: [0.7, 1.5]
                  }}
                  transition={{
                    duration: 5,
                    delay: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <Typography variant="h4" sx={{ filter: 'blur(7px)', userSelect: 'none', opacity: 0.5 }}>
                    ☁️
                  </Typography>
                </motion.div>
              </Box>

        {/* --- KÁVÉ IKON --- */}
        <FaCoffee size={70} style={{ color: '#fff', marginBottom: '16px', zIndex: 1 }} />

        {/* --- IDŐZÍTŐ --- */}
        <Typography variant="h2" sx={{ fontWeight: 'bold', fontFamily: 'monospace', color: 'white' }}>
          {Math.floor(remainingTime / 60)}:{(remainingTime % 60).toString().padStart(2, '0')}
        </Typography>
        
        <Typography variant="body1" sx={{ mt: 2, color: 'rgba(255,255,255,0.8)', fontWeight: '500' }}>
          Have a good rest!
        </Typography>
        <Typography variant="caption" sx={{ mt: 1, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
          Brewing relaxation...
        </Typography>
      </Box>
    )}
  </DialogContent>

  {/* --- DINAMIKUS AKCIÓK --- */}
  <DialogActions sx={{ justifyContent: 'center', pb: 3, minHeight: '52px' }}>
    {!timerActive ? (
      /* Csak akkor látszik a gomb, ha NEM megy a szünet */
      <Button 
        onClick={() => setOpenDialog(null)} 
        sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}
      >
        Close Settings
      </Button>
    ) : (
      /* Ha megy a szünet, üresen hagyjuk vagy tehetünk ide egy kis üzenetet */
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
        Take your time...
      </Typography>
    )}
  </DialogActions>
</AnimatedDialog>

      {/* --- LAPTOP SETTINGS DIALOG --- */}
      <AnimatedDialog
  open={openDialog === 'laptop'}
  // Csak a Dialog-ot zárjuk be, az adatokat még NEM töröljük
  onClose={() => setOpenDialog(null)} 
  disableScrollLock
  darkMode={darkMode}
  TransitionComponent={Zoom}
  transitionDuration={400}
>
  <DialogTitle sx={{ color: '#fff' }}> {/* Light módban is fehér marad */}
    <LaptopIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> System Settings
  </DialogTitle>
  <DialogContent>
    <Box sx={{ mt: 1 }}>
      {renderLaptopDialogContent()}
    </Box>
  </DialogContent>
  <DialogActions sx={{ justifyContent: 'center' }}>
    <Button 
      onClick={() => setOpenDialog(null)} 
      sx={{ color: 'rgba(255,255,255,0.7)' }}
    >
      Close Settings
    </Button>
  </DialogActions>
</AnimatedDialog>

      <AnimatedDialog 
  open={openDialog === 'pencil'} 
  onClose={handleDialogClose} 
  disableScrollLock
  darkMode={darkMode}
  maxWidth="sm"
  fullWidth
  // --- Animáció hozzáadása ---
  TransitionComponent={Zoom}
  transitionDuration={400}
>
  <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', color: 'white', pt: 3 }}>
    Update Content Details
  </DialogTitle>
  
  <DialogContent>
    {drawingToolsData.length > 0 ? (
      <Stack spacing={2} sx={{ mt: 1 }}>
        <FormControl fullWidth variant="filled">
          <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Select Item</InputLabel>
          <Select
            value={selectedDrawingTool?.id || ''}
            onChange={(e) => handleDrawingToolSelect(Number(e.target.value))}
            sx={{ 
              borderRadius: '12px', 
              bgcolor: 'rgba(255,255,255,0.1)', 
              color: 'white',
              '& .MuiSvgIcon-root': { color: 'white' } // A legördülő nyíl is legyen fehér
            }}
          >
            {drawingToolsData.map((tool) => (
              <MenuItem key={tool.id} value={tool.id}>{tool.title}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedDrawingTool && editedTool && (
          <>
            <StyledTextField 
              darkMode={darkMode} 
              label="Title" 
              fullWidth 
              variant="filled"
              value={editedTool.title} 
              onChange={(e) => setEditedTool({ ...editedTool, title: e.target.value })}
              InputProps={{ disableUnderline: true, style: { color: 'white' } }}
              InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
            />
            
            <StyledTextField 
              darkMode={darkMode} 
              label="Category" 
              fullWidth 
              variant="filled"
              value={editedTool.category} 
              onChange={(e) => setEditedTool({ ...editedTool, category: e.target.value })} 
              InputProps={{ disableUnderline: true, style: { color: 'white' } }}
              InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
            />
            
            <StyledTextField 
              darkMode={darkMode} 
              label="URL" 
              fullWidth 
              variant="filled"
              value={editedTool.url} 
              error={urlError}
              helperText={urlError ? "Invalid URL format" : ""}
              onChange={(e) => {
                const val = e.target.value;
                setEditedTool({ ...editedTool, url: val });
                setUrlError(val !== "" && !isValidUrl(val));
              }} 
              InputProps={{ disableUnderline: true, style: { color: 'white' } }}
              InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
              FormHelperTextProps={{ style: { color: '#ff8a80' } }} // Hibaüzenet színe
            />

            <StyledTextField 
              darkMode={darkMode} 
              label="Description" 
              fullWidth 
              variant="filled"
              multiline 
              rows={2}
              value={editedTool.shortDescription} 
              onChange={(e) => setEditedTool({ ...editedTool, shortDescription: e.target.value })} 
              InputProps={{ disableUnderline: true, style: { color: 'white' } }}
              InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
            />
          </>
        )}
      </Stack>
    ) : (
      <Typography sx={{ color: 'white', textAlign: 'center', py: 4, opacity: 0.6 }}>
        No contents to edit.
      </Typography>
    )}
  </DialogContent>

  <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3, gap: 2 }}>
    <Button 
      onClick={handleDialogClose} 
      sx={{ 
        color: 'white', 
        opacity: 0.7, 
        textTransform: 'none',
        '&:hover': { opacity: 1 }
      }}
    >
      Maybe later
    </Button>
    <DialogActionButton 
      darkMode={darkMode} 
      disabled={!isFormValid()} 
      onClick={() => setConfirmDialogOpen("update")}
      sx={{ px: 4 }}
    >
      Update Content
    </DialogActionButton>
  </DialogActions>
</AnimatedDialog>

      {/* --- CONFIRM DIALOG --- */}
      <AnimatedDialog 
        open={Boolean(confirmDialogOpen)} 
        onClose={() => setConfirmDialogOpen(null)}
        darkMode={darkMode}
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography sx={{ textAlign: 'center' }}>
            Are you sure you want to <b>{confirmDialogOpen}</b> this content?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={() => setConfirmDialogOpen(null)} sx={{ color: 'white' }}>Go Back</Button>
          <DialogActionButton 
            darkMode={darkMode}
            onClick={confirmDialogOpen === "update" ? handleConfirmUpdate : handleConfirmDelete}
            sx={{ bgcolor: confirmDialogOpen === 'delete' ? 'rgba(255,23,68,0.2)' : 'rgba(255,255,255,0.1)' }}
          >
            Confirm
          </DialogActionButton>
        </DialogActions>
      </AnimatedDialog>

      {/* Snackbar & Book Dialog */}
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: '10px' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Book Dialog (Favorite Content) */}
      <AnimatedDialog 
        open={openDialog === 'book'} 
        onClose={() => setOpenDialog(null)}
        darkMode={darkMode}
        maxWidth="md"
        fullWidth
        // --- Ugyanaz az animációs logika, mint a Coffee-nál ---
        TransitionComponent={Zoom}
        transitionDuration={400}
        disableScrollLock
      >
        <DialogTitle sx={{ color: 'white' }}>
          <BookmarkIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Favorites Collection
        </DialogTitle>
        
        <DialogContent>
          {favoritesData.length > 0 ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {favoritesData.map((item) => (
                <Box 
                  key={item.id} 
                  sx={{ 
                    p: 2, 
                    borderRadius: '16px', // Kicsit kerekebb, modernebb
                    bgcolor: 'rgba(255,255,255,0.08)', // Jobb láthatóság
                    border: '1px solid rgba(255,255,255,0.15)', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.12)',
                      transform: 'scale(1.01)'
                    }
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'white' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                      {item.category}
                    </Typography>
                  </Box>
                  
                  <Stack direction="row" spacing={1}>
                    <IconButton 
                      size="small" 
                      onClick={() => window.open(formatUrl(item.url), '_blank')} 
                      sx={{ 
                        color: 'white', 
                        bgcolor: 'rgba(255,255,255,0.1)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                      }}
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => switchFavorite(item.id)} 
                      sx={{ 
                        color: 'gold',
                        bgcolor: 'rgba(255,215,0,0.1)',
                        '&:hover': { bgcolor: 'rgba(255,215,0,0.2)' }
                      }}
                    >
                      <StarIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography sx={{ textAlign: 'center', py: 6, color: 'rgba(255,255,255,0.5)' }}>
              No favorites added yet.
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            onClick={() => setOpenDialog(null)} 
            sx={{ 
              color: 'white', 
              textTransform: 'none',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '12px',
              px: 4,
              '&:hover': {
                border: '1px solid white',
                bgcolor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </AnimatedDialog>

      {/* Full screen laptop update view */}
      {showLaptopFullScreen && (
        <Dialog fullScreen open>
           {renderLaptopUpdateScreen()}
        </Dialog>
      )}

      {!timerActive && !showLaptopFullScreen && (
        <CursorTrail
          style={{ left: mousePosition.x - 10, top: mousePosition.y - 10 }}
          animate={{ opacity: [0.5, 0], scale: [1, 2], background: '#00000020' }}
          transition={{ duration: 0.5 }}
        />
      )}

      <LeftPanel
        isAuthenticated={isAuthenticated}
        onLoginRequired={() => navigate('/login')}
        onNavigation={handlePanelAction} 
        open={leftPanelOpen}
        onToggle={() => setLeftPanelOpen((prev) => !prev)}
      />
    </div>
);
};

export default Mystudydesk;
